import React, { useEffect, useRef, useState } from 'react'
import { calculateTickFromPrice, findClosestTick } from '../../../blockchainUtils/math'
import { usePrevious } from '../../../hooks/usePrevious'
import { DragSliderIcon } from '../../../svgIcons/DragSliderIcon'
import { ArrowDown } from '../../../svgIcons/ArrowDownIcon'
import { fetchRangeDataForPool } from '../../charts/chartsApi'
import RangeSliderChart from './RangeSliderChart'
import { useWalletContext } from '../../WalletContext'
import { GAtrack } from '../../../services/ga'
import { getArrayRange } from '../../charts/chartUtils'
import { Network } from '../../../services/token.service'
import { ZoomInIcon } from '../../../svgIcons/ZoomInIcon'
import { ZoomOutIcon } from '../../../svgIcons/ZoomOutIcon'
import { MAX_TICK } from '../helper'

interface Position {
    x: number
}

const maxZoomIn = 4
const zoomPercent = 20

function RangeSliderSelector(props: {
    minTick: number
    rangeSelected?: number
    maxTick: number
    currentTick: number
    tickAmount: number
    currentRatio: number
    onUpdateMaxTick: (maxTick: number) => void
    onUpdateMinTick: (minTick: number) => void
    onUpdateCurrentTick?: (tickChange: number) => void
    className?: string
    isReversed?: boolean
    maxMultiplier?: number
    minDivider?: number
    currentPrice?: number
    defaultZoomLevel?: 1 | 2 | 3 | 4
    poolId: string
    isViewOnly?: boolean
}) {
    const {
        currentPrice,
        maxMultiplier = 2.25,
        minDivider = 1.5,
        minTick,
        maxTick,
        currentTick,
        className = '',
        onUpdateMinTick,
        onUpdateMaxTick,
        currentRatio,
        tickAmount,
        isReversed,
        rangeSelected,
        poolId,
        isViewOnly = false,
        defaultZoomLevel
        // selectedMax
    } = props
    const { networkName: network } = useWalletContext()
    const [minTickState, setMinTickState] = useState(minTick)
    const [maxTickState, setMaxTickState] = useState(maxTick)
    const [dragging, setDragging] = useState<boolean>(false)
    const [minPosition, setMinPosition] = useState<Position>({ x: isReversed ? 50 : 0 })
    const [maxPosition, setMaxPosition] = useState<Position>({ x: isReversed ? 100 : 50 })
    const containerRef = useRef<HTMLDivElement>(null)
    const draggableMinRef = useRef<HTMLDivElement>(null)
    const draggableMaxRef = useRef<HTMLDivElement>(null)
    const [draggingDirection, setDraggingDirection] = useState(undefined as unknown as 'min' | 'max')
    const previousIsReversed = usePrevious(isReversed)
    const [rangeData, setRangeData] = useState([] as { tick: number; liq: number }[])
    const [zoomedData, setZoomedData] = useState([] as { tick: number; liq: number }[])
    const [fullDataRange, setFullDataRange] = useState([] as { tick: number; liq: number }[])
    const [zoomLevel, setZoomLevel] = useState(defaultZoomLevel ? defaultZoomLevel : 0)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        if (!rangeSelected) return
        if (rangeSelected && rangeSelected <= 0.5) return setZoomLevel(4)
        if (rangeSelected && rangeSelected <= 1.5) return setZoomLevel(4)
        if (rangeSelected && rangeSelected <= 6) return setZoomLevel(2)
        else setZoomLevel(0)
    }, [rangeSelected])

    useEffect(() => {
        if (poolId) loadPoolRangeData()
    }, [poolId])

    useEffect(() => {
        if (fullDataRange) updateRange()
    }, [poolId])

    useEffect(() => {
        document.addEventListener('mouseup', handleMouseUp)
        return () => {
            document.removeEventListener('mouseup', handleMouseUp)
        }
    }, [])

    useEffect(() => {
        setMinTickState(minTick)
        setMaxTickState(maxTick)
    }, [minTick, maxTick])

    useEffect(() => {
        if (minTickState !== null && minTickState !== undefined && maxTickState !== undefined && maxTickState !== null) {
            const minTickPercentage = isReversed ? getPercentage(maxTickState) : getPercentage(minTickState)
            const maxTickPercentage = isReversed ? getPercentage(minTickState) : getPercentage(maxTickState)
            setMinPosition({ x: minTickPercentage })
            setMaxPosition({ x: maxTickPercentage })
        }
    }, [minTickState, maxTickState, isReversed, previousIsReversed, zoomLevel, zoomedData, currentRatio])

    // useEffect(() => {
    //     if (selectedMin && selectedMax) {
    //         const minTickPercentage = isReversed ? getPercentage(selectedMin) : getPercentage(selectedMax)
    //         const maxTickPercentage = isReversed ? getPercentage(selectedMax) : getPercentage(selectedMin)
    //         setMinPosition({ x: minTickPercentage })
    //         setMaxPosition({ x: maxTickPercentage })
    //     }
    // }, [selectedMin, selectedMax])

    useEffect(() => {
        if (fullDataRange) updateRange()
    }, [fullDataRange])

    useEffect(() => {
        if (rangeData && rangeData.length > 0) onZoomLevelChange()
    }, [zoomLevel, rangeData])

    async function updateRange() {
        if (!currentRatio) return
        setRangeData([])
        const minTickFromPrice = findClosestTick(calculateTickFromPrice(currentRatio / minDivider, tickAmount), tickAmount)
        const maxTickFromPrice = findClosestTick(calculateTickFromPrice(currentRatio * maxMultiplier, tickAmount), tickAmount)
        const rangedArray = getArrayRange(fullDataRange, minTickFromPrice, maxTickFromPrice)
        if (network === 'mainnet-alpha') GAtrack(`range_chart_loaded_${poolId}`, `range_chart_loaded_${poolId}`, 'add_liquidity')

        setRangeData(rangedArray)
        setZoomedData(rangedArray)
    }

    async function onZoom(isZoomIn: boolean) {
        if ((zoomLevel === 0 && !isZoomIn) || (zoomLevel === maxZoomIn && isZoomIn)) return
        // if (isZoomIn) {
        const newZoomLeve = isZoomIn ? zoomLevel + 1 : zoomLevel - 1
        setZoomLevel(newZoomLeve)
        const currentData = [...rangeData]
        const rangeLength = currentData.length
        const newRangeLength = (rangeLength - ((newZoomLeve * zoomPercent) / 100) * rangeLength).toFixed(0)
        const diff = rangeLength - Number(newRangeLength)
        const newRange = currentData.slice(Number((diff / 2).toFixed(0)), rangeLength - Number((diff / 2).toFixed(0)))
        setZoomedData(newRange)
    }

    function onZoomLevelChange() {
        const currentData = [...rangeData]
        const rangeLength = currentData.length
        const newRangeLength = (rangeLength - ((zoomLevel * zoomPercent) / 100) * rangeLength).toFixed(0)
        const diff = rangeLength - Number(newRangeLength)
        const newRange = currentData.slice(Number((diff / 2).toFixed(0)), rangeLength - Number((diff / 2).toFixed(0)))
        setZoomedData(newRange)
    }

    async function loadPoolRangeData() {
        if (network !== 'mainnet-alpha' || !currentRatio) return

        // if (network !== 'mainnet-alpha') return
        setIsLoading(true)
        try {
            const response = await fetchRangeDataForPool(poolId)
            setRangeData([])
            setFullDataRange(response as any)
            const minTickFromPrice = findClosestTick(calculateTickFromPrice(currentRatio / minDivider, tickAmount), tickAmount)
            const maxTickFromPrice = findClosestTick(calculateTickFromPrice(currentRatio * maxMultiplier, tickAmount), tickAmount)
            const rangedArray = getArrayRange(response as any, minTickFromPrice, maxTickFromPrice)
            setRangeData(rangedArray)
            setIsLoading(false)
        } catch (e) {
            setIsLoading(false)
        }
    }

    // const getArrayRange = (dataArray: { TICK: number; AMOUNT_TICK_MULTIPLIED: number }[], minTick: number, maxTick: number) => {
    //     const startIndex = dataArray.findIndex((item) => item.TICK - MAX_TICK >= minTick)
    //     const endIndex = dataArray.findIndex((item) => item.TICK - MAX_TICK > maxTick)

    //     if (startIndex === -1 || endIndex === -1) {
    //         return []
    //     }

    //     let resultArray = dataArray.slice(startIndex, endIndex)
    //     const maxLength = 400

    //     if (resultArray.length > maxLength) {
    //         const step = Math.ceil(resultArray.length / maxLength)
    //         resultArray = resultArray.filter((_, index) => index % step === 0)
    //     }

    //     return resultArray
    // }

    const handleMouseDown = (e: any, direction: 'min' | 'max') => {
        e.preventDefault()
        setDraggingDirection(direction)
        setDragging(true)
    }

    useEffect(() => {
        if (previousIsReversed !== undefined && previousIsReversed !== isReversed) {
            setMinPosition({ x: 100 - maxPosition.x })
            setMaxPosition({ x: 100 - minPosition.x })
        }
    }, [isReversed])

    const handleMouseMove = (e: any) => {
        if (isViewOnly) return
        if (dragging) {
            const slider = containerRef.current
            if (!slider) return
            const sliderRect = slider.getBoundingClientRect()
            const percentageX = ((e.clientX - sliderRect.left) / sliderRect.width) * 100

            if (percentageX === 0 || percentageX > 100) return
            if (draggingDirection === 'min' && percentageX >= maxPosition.x) {
                setDragging(false)
                return
            }
            if (draggingDirection === 'min' && percentageX < 0) {
                return
            }
            if (draggingDirection === 'max' && percentageX <= minPosition.x) {
                setDragging(false)
                return
            }
            if (percentageX !== 0) {
                // if (draggingDirection === 'min') {
                //     if (percentageX < maxPosition.x) {
                //         setMinPosition({ x: percentageX })
                //     }
                // } else {
                //     if (percentageX > minPosition.x) {
                //         setMaxPosition({ x: percentageX })
                //     }
                // }
                const tick = calculatePosition(percentageX)
                if (draggingDirection === 'min' && !isReversed) onUpdateMinTick(tick)
                if (draggingDirection === 'min' && isReversed) onUpdateMaxTick(tick)
                if (draggingDirection === 'max' && !isReversed) onUpdateMaxTick(tick)
                if (draggingDirection === 'max' && isReversed) onUpdateMinTick(tick)
            }
        }
    }

    function calculatePosition(percentageX: number) {
        const range = getRangeAbsolute()
        let tick

        if (isReversed) tick = getMaxRange() - (range * percentageX) / 100
        else tick = getMinRange() + (range * percentageX) / 100
        return tick
    }

    const handleMouseUp = () => {
        setDragging(false)
    }

    function getPercentage(tick: number) {
        if (!currentRatio) return 0
        const perc = ((tick - getMinRange()) * 100) / getRangeAbsolute()
        if (isReversed) return 100 - perc
        return perc
    }

    function getRangeAbsolute() {
        return Math.abs(getMaxRange() - getMinRange())

        //     if (Math.abs(getMaxRange()) > Math.abs(getMinRange())) return Math.abs(getMaxRange()) - getMinRange()
        //     // else return Math.abs(getMinRange()) - getMaxRange()
        // // else if (getMaxRange() < 0 && getMinRange() )
        //     else return Math.abs(getMinRange()) - Math.abs(getMaxRange())
    }

    function getMaxRange() {
        if (zoomLevel > 0 && zoomedData && zoomedData.length > 0) return zoomedData[zoomedData.length - 1].tick - MAX_TICK
        const maxTickFromPrice = calculateTickFromPrice(currentRatio * maxMultiplier, tickAmount)
        const closestMaxTick = findClosestTick(maxTickFromPrice, tickAmount)
        return closestMaxTick
    }

    function getMinRange() {
        if (zoomLevel > 0 && zoomedData && zoomedData.length > 0) return zoomedData[0].tick - MAX_TICK
        const minTickFromPrice = calculateTickFromPrice(currentRatio / minDivider, tickAmount)

        const closestMinTick = findClosestTick(minTickFromPrice, tickAmount)
        return closestMinTick
    }

    function renderMinTick() {
        if (minPosition.x < 0) {
            return (
                <div className='hidden lg:flex w-[14px] h-[22px] flex items-center justify-center bg-[#0C3F6D] absolute top-[24px] left-[0px] rounded-[4px]'>
                    <ArrowDown style={{ transform: 'rotate(90deg)' }} className='w-[10px] h-[10px] text-[white]' />
                </div>
            )
        }
        if (minPosition.x > 100) {
            return (
                <div className='hidden lg:flex w-[14px] h-[22px] flex items-center justify-center bg-[#0C3F6D] absolute top-[24px] right-[0px] rounded-[4px]'>
                    <ArrowDown style={{ transform: 'rotate(-90deg)' }} className='w-[10px] h-[10px] text-[white]' />
                </div>
            )
        }

        return (
            <div
                ref={draggableMinRef}
                onMouseUp={handleMouseUp}
                onMouseDown={(e) => handleMouseDown(e, 'min')}
                className={`mb-2 hidden lg:flex shadow-2xl z-[2] ${dragging ? '' : 'transition-all'}`}
                style={{
                    position: 'absolute',
                    left: `${minPosition.x}%`,
                    bottom: '0px',
                    width: '4px',
                    height: '100%',
                    background: '#0C3F6D',
                    cursor: 'grab'
                }}>
                <DragSliderIcon className='select-none absolute top-0 right-0' />
                <span className='select-none absolute top-[-4px] border-[1px] bg-[#0C3F6D] border-[#0C3F6D] rounded-full py-1 px-2 left-[-56px] text-[12px] text-center'>Min.</span>
            </div>
        )
    }

    function renderCurrentTick() {
        if (!currentTick) return null
        return (
            <div
                className={`${
                    network !== 'mainnet-alpha' ? 'hidden lg:flex' : ''
                } transition-all mb-2  absolute bottom-[-2px] border-r-[1px] border-[#6F6E8F] border-dashed h-[100%] `}
                style={{ left: `50%` }}>
                {/* style={{ left: `${getPercentage(currentTick)}%` }}> */}

                <span className='lg:w-[220px] w-[180px] z-[10] absolute bottom-[-16px] text-[12px] text-center left-[-68px]'>{`Current: ${currentPrice}`}</span>
            </div>
        )
    }

    function renderMaxTick() {
        if (maxPosition.x < 0) {
            return (
                <div className='hidden lg:flex w-[14px] h-[22px] flex items-center justify-center bg-[#0C3F6D] absolute top-[0px] left-[0px] rounded-[4px]'>
                    <ArrowDown style={{ transform: 'rotate(90deg)' }} className='w-[10px] h-[10px] text-[white]' />
                </div>
            )
        }
        if (maxPosition.x > 100)
            return (
                <div className='hidden lg:flex w-[14px] h-[22px] flex items-center justify-center bg-[#0C3F6D] absolute top-[0px] right-[0px] rounded-[4px]'>
                    <ArrowDown style={{ transform: 'rotate(-90deg)' }} className='w-[10px] h-[10px] text-[white]' />
                </div>
            )
        return (
            <div
                ref={draggableMaxRef}
                draggable={true}
                onMouseDown={(e) => handleMouseDown(e, 'max')}
                className={`mb-2 hidden lg:flex shadow-2xl z-[2] ${dragging ? '' : 'transition-all'}`}
                style={{
                    position: 'absolute',
                    left: `${maxPosition.x}%`,
                    bottom: '0px',
                    width: '4px',
                    height: '100%',
                    background: '#0C3F6D',
                    cursor: 'grab'
                }}>
                <DragSliderIcon className='absolute top-0 left-0' />
                <span className='absolute top-[-4px] border-[1px] bg-[#0C3F6D] border-[#0C3F6D] rounded-full py-1 px-2 right-[-58px] text-[12px] text-center'>Max.</span>
            </div>
        )
    }

    function renderRangeChart() {
        // const data = isReversed ? rangeData.reverse() : rangeData;
        return <RangeSliderChart chartHeight={300} network={network as Network} isReversed={isReversed} data={zoomedData} />
    }

    return (
        <div className='relative'>
            {rangeData && rangeData.length > 0 ? (
                <div className='relative flex w-full justify-end z-[100] top-[-52px]'>
                    <div className='flex items-center'>
                        <span
                            onClick={() => onZoom(true)}
                            className='w-[24px] h-[24px] text-white border-[2px] border-[#0C3F6D] bg-[transparent] hover:border-[#366FB7] focus:border-[#0AE8F9] shadow-xl rounded-full text-[18px] mr-2 flex items-center justify-center cursor-pointer'>
                            <ZoomInIcon className='w-[16px] h-[16px] text-white' />
                        </span>

                        <span
                            onClick={() => onZoom(false)}
                            className='w-[24px] h-[24px] text-white border-[2px] border-[#0C3F6D] bg-[transparent] hover:border-[#366FB7] focus:border-[#0AE8F9] shadow-xl rounded-full text-[18px] mr-2 flex items-center justify-center cursor-pointer'>
                            <ZoomOutIcon className='w-[14px] h-[14px] text-white' />
                        </span>
                    </div>
                </div>
            ) : null}
            {renderRangeChart()}
            <div
                ref={containerRef}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                className={`absolute z-[10] left-0 top-0 right-0 bottom-[20px] w-full justify-between flex  items-center ${className}`}>
                {/* className={`absolute z-[10] left-0 top-0 right-0 bottom-[20px] hidden lg:flex w-full justify-between flex  items-center ${className}`}> */}
                {renderMinTick()}
                {renderMaxTick()}
                {renderCurrentTick()}
                <div className='hidden lg:flex absolute z-[-1] bg-black opacity-[0.3]' />
                <div className='hidden lg:flex mb-2 absolute w-full h-[2px] bg-[#0C3F6D] left-0 bottom-0' />
            </div>
        </div>
    )
}

export default RangeSliderSelector
